import {useCallback, useContext, useEffect, useState} from 'react';
import {Button, Divider, Form, Steps} from 'antd';
import {FixedLengthArray, ModalFormFieldTypes} from 'components/formItems/FormGroup/FormGroup';
import {CANDIDATES_FORM} from './candidateForm';
import {CaretLeft, CaretRight} from 'phosphor-react';
import {AuthContext} from 'context/Auth.context';
import {useNavigate} from 'react-router-dom';
import FormGroup from 'components/formItems/FormGroup';
import FullScreenLoading from 'components/FullScreenLoading';
import Toast from 'components/Toast';
import useMediaQuery from 'hooks/useMediaQuery.hook';
import ConfirmModal from 'components/ConfirmModal';
import SERVICES from 'services';
import moment from 'moment';
import _ from 'lodash';
import './Admission.less';
import {CandidateContext} from 'context/Candidate.context';
import API from 'api';

interface ModalFormTypes {
	name: string;
	fields: ModalFormFieldTypes[];
	visibility?: Array<'ADD' | 'EDIT' | string>;
	errorStatus?: boolean;
}

const {Step} = Steps;

const validateMessages = {
	required: 'Դաշտը պարտադիր է:',
	types: {
		email: '${label} անվավեր է:',
		number: '${label} վավեր թիվ չէ:'
	}
};

const pagesKeys = ['personal', 'parental', 'education', 'survey', 'admission', 'documents'];

const Admission = ({role}: {role: 'STUDENT' | 'CANDIDATE'}) => {
	const isStepper = true;
	const [current, setCurrent] = useState(0);
	const [admissionFacultyIDs, setAdmissionFacultyIDs] = useState<any[]>([1]);
	const [formErrors, setFormErrors] = useState<any>({});
	const [formInitialValues, setFormInitialValues] = useState<Object>({});
	const [loading, setLoading] = useState(true);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [cantApply, setCantApply] = useState('');
	const isMobile = useMediaQuery('(max-width: 768px)');
	const [modalForm, setModalForm] = useState<ModalFormTypes[] | undefined>(
		CANDIDATES_FORM(role, {admissionFacultyIDs}) as ModalFormTypes[]
	);

	const {state: candidateState, dispatch: candidateDispatch} = useContext(CandidateContext);

	const statusID = role === 'CANDIDATE' ? candidateState.statusID : 3;

	const navigate = useNavigate();

	const [form] = Form.useForm();

	const showSuccessMessage = (text: string) => {
		Toast.success(text);
	};

	const getAdmissionFaculties = useCallback(() => {
		SERVICES.SETTINGS.getByCustomPath(
			API[role].USER.CUSTOM_API +
				`faculties?pagination=0&status=1&admissionYear=${moment(Date.now()).format('YYYY')}`
		).then(res => {
			setAdmissionFacultyIDs(res.flatMap((el: any) => (el.facultyID === 2 ? [el.id] : [])));
		});
	}, []);

	const prev = () => {
		setCurrent(prev => {
			document.querySelector('.admission')?.scrollTo({top: 0, behavior: 'smooth'});
			document.querySelector('.steps')?.scrollTo({left: (prev - 1) * 125, behavior: 'smooth'});
			return prev - 1;
		});
	};

	const next = async () => {
		if (statusID !== 1) {
			setCurrent(prev => prev + 1);
			return;
		}

		setFormErrors({});
		setButtonLoading(true);

		try {
			const validatedValues = await form.validateFields();

			let newValues = {...formInitialValues, ...{...validatedValues}};

			let needToUpdate = false;

			for (const key in validatedValues) {
				if (Object.prototype.hasOwnProperty.call(validatedValues, key)) {
					const initialValuesMap = new Map(Object.entries(formInitialValues));
					if (
						!initialValuesMap.has(key) ||
						(initialValuesMap.has(key) &&
							!_.isEqual(validatedValues[key], initialValuesMap.get(key)))
					) {
						needToUpdate = true;
						break;
					}
				}
			}

			if (!needToUpdate && pagesKeys[current] !== 'documents') {
				setCurrent(prev => prev + 1);
				setButtonLoading(false);
				return;
			}

			try {
				const patchResponse = await SERVICES.USER.patchCandidateData(
					pagesKeys[current],
					validatedValues,
					pagesKeys[current] === 'documents'
				);

				setFormInitialValues(newValues);

				if (pagesKeys[current] !== 'documents') {
					setCurrent(prev => prev + 1);
				}

				if (pagesKeys[current] === 'documents') {
					showSuccessMessage(patchResponse);
					const getProfileResponse = await SERVICES.USER.getProfile(role);
					candidateDispatch({
						type: 'SET_STATUS',
						payload: {
							status: getProfileResponse.data.status,
							statusID: getProfileResponse.data.statusID
						}
					});
					navigate('/');
					document.querySelector('.admission')?.scroll({top: 0, behavior: 'smooth'});
					document.querySelector('.steps')?.scroll({left: current * 175, behavior: 'smooth'});
				}
			} catch (patchError: any) {
				if (typeof patchError.message === 'string') {
					Toast.error(patchError.message);
				} else {
					let newMessage = _.cloneDeep(patchError.message);

					if (typeof newMessage?.admissionFiles === 'string') {
						Toast.error(newMessage.admissionFiles);
						setButtonLoading(false);
						return;
					}

					if (newMessage?.admissionFiles) {
						for (const key in newMessage.admissionFiles) {
							if (
								Object.prototype.hasOwnProperty.call(newMessage.admissionFiles, key) &&
								typeof newMessage.admissionFiles[key] === 'string'
							) {
								newMessage.admissionFiles[key] = {
									path: newMessage.admissionFiles[key]
								};
							}
						}
					}

					setFormErrors(newMessage);
				}
			}
		} catch (validationError: any) {
			if (validationError?.errorFields) {
				document
					.querySelector(`#${validationError.errorFields[0].name.join('_')}`)
					?.scrollIntoView({behavior: 'smooth'});
			}
		}

		setButtonLoading(false);
	};

	const _next = () => {
		if (statusID !== 1) {
			setCurrent(current + 1);
			return;
		}
		setFormErrors({});
		form.validateFields()
			.then(values => {
				let newValues = {...formInitialValues, ...{...values}};

				let needToUpdate = false;

				for (const key in values) {
					if (Object.prototype.hasOwnProperty.call(values, key)) {
						const initialValuesMap = new Map(Object.entries(formInitialValues));
						if (
							!initialValuesMap.has(key) ||
							(initialValuesMap.has(key) && !_.isEqual(values[key], initialValuesMap.get(key)))
						) {
							needToUpdate = true;
							break;
						}
					}
				}

				if (!needToUpdate && pagesKeys[current] !== 'documents') {
					setCurrent(current + 1);
					return;
				}

				SERVICES.USER.patchCandidateData(
					pagesKeys[current],
					values,
					pagesKeys[current] === 'documents'
				)
					.then(res => {
						setFormInitialValues(newValues);

						if (pagesKeys[current] !== 'documents') {
							setCurrent(current + 1);
						}

						if (pagesKeys[current] === 'documents') {
							showSuccessMessage(res);
							SERVICES.USER.getProfile(role)
								.then(res => {
									candidateDispatch({
										type: 'SET_STATUS',
										payload: {
											status: res.data.status,
											statusID: res.data.statusID
										}
									});
								})
								.then(() => {
									navigate('/');
									return;
								});
							document.querySelector('.admission')?.scroll({top: 0, behavior: 'smooth'});
							document
								.querySelector('.steps')
								?.scroll({left: current * 175, behavior: 'smooth'});
						}
					})
					.catch(err => {
						if (typeof err.message === 'string') {
							Toast.error(err.message);
						} else {
							let newMessage = _.cloneDeep(err.message);

							if (newMessage?.admissionFiles) {
								for (const key in newMessage.admissionFiles) {
									if (
										Object.prototype.hasOwnProperty.call(newMessage.admissionFiles, key)
									) {
										newMessage.admissionFiles[key] = {
											path: newMessage.admissionFiles[key]
										};
									}
								}
							}

							setFormErrors(newMessage);
						}
					});
			})
			.catch(e => {
				if (e?.errorFields) {
					document
						.querySelector(`#${e.errorFields[0].name.join('_')}`)
						?.scrollIntoView({behavior: 'smooth'});
				}
			});
	};

	const onSubmit = () => {
		ConfirmModal({
			title: 'Վստա՞հ եք, որ ցանկանում եք հաստատել դիմում-հայտը։',
			onSubmit: next,
			content: '«Հաստատել (Submit)» կոճակը սեղմելուց հետո ոչ մի փոփոխություն կատարել չեք կարողանա:'
		});
	};

	const onValuesChange = (changedValues: any) => {
		const changedKey = Object.keys(changedValues)[0];
		if (formErrors[changedKey]) {
			setFormErrors((prev: any) => {
				const errors = {...prev};
				delete errors[changedKey];
				return errors;
			});
		}
		if (changedValues.admissionFaculties) {
			form.setFieldsValue({optionalSubjects: [], admissionLanguageExamID: null});
		}
	};

	const formBlurHandler = (e: React.ChangeEvent) => {
		form.validateFields([e.target.id]).then(values => {
			for (const key in values) {
				if (Object.prototype.hasOwnProperty.call(values, key)) {
					if (typeof values[key] === 'string') {
						const trimed = values[key].trim();
						if (trimed !== values[key]) {
							form.setFieldsValue({[e.target.id]: trimed});
							form.validateFields([e.target.id]);
						}
					}
				}
			}
		});
	};

	useEffect(() => {
		setLoading(true);
		SERVICES.AUTH.checkAdmission()
			.catch(e => {
				setCantApply(e.message);
			})
			.finally(() => {
				SERVICES.SETTINGS.fetchOptions('candidateUniversityExpectations', role).then(res => {
					const universityExpectations: any[] = res.map(opt => ({
						universityExpectationsID: opt.id,
						label: opt.name
					}));

					let data: any = {
						universityExpectations,
						education: [''],
						admissionYear: moment(Date.now()).format('YYYY'),
						admissionFiles: [
							{typeID: 2},
							{typeID: 3},
							{typeID: 4},
							{typeID: 5},
							{typeID: 8},
							{typeID: 6}
						]
					};

					SERVICES.USER.getAdmissionData(role)
						.then((res: any) => {
							if (res.data) {
								let fetchedData = {...res.data};

								for (const key in fetchedData) {
									if (Object.prototype.hasOwnProperty.call(fetchedData, key)) {
										const value = fetchedData[key];
										if (!value) {
											delete fetchedData[key];
										}
										if (Array.isArray(value) && !value.length) {
											delete fetchedData[key];
										}
									}
								}

								if (fetchedData?.admissionFiles?.length) {
									fetchedData.admissionFiles = fetchedData.admissionFiles.filter(
										(i: any) => i.typeID !== 1
									);
								}

								data = {...data, ...formInitialValues, ...fetchedData};
							}

							setFormInitialValues((prev: any) => {
								return {
									...prev,
									...data,
									openDoorsParticipation: data.openDoorsParticipation || 0
								};
							});
						})
						.finally(() => {
							setLoading(false);
						});
				});
			});
	}, []);

	useEffect(() => {
		if (statusID === 1) {
			let interval = setInterval(() => {
				const time = localStorage.getItem('time/admission');
				localStorage.setItem(`time${location.pathname}`, time ? (+time + 1).toString() : '1');
			}, 1000);

			return () => {
				clearInterval(interval);
			};
		}
	}, []);

	useEffect(() => {
		getAdmissionFaculties();
	}, []);

	useEffect(() => {
		setModalForm(CANDIDATES_FORM(role, {admissionFacultyIDs}) as ModalFormTypes[]);
	}, [admissionFacultyIDs]);

	if (loading) return <FullScreenLoading />;

	if (statusID === 1 && !!cantApply)
		return (
			<div
				style={{
					width: '100vw',
					height: '80vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center'
				}}>
				<span
					style={{
						fontSize: 18,
						color: '#38456F',
						textTransform: 'uppercase'
					}}>
					{cantApply}
				</span>
			</div>
		);

	return (
		<Form
			form={form}
			layout="vertical"
			validateMessages={validateMessages}
			className="admission"
			onBlur={formBlurHandler}
			initialValues={formInitialValues}
			onValuesChange={onValuesChange}>
			<h1
				style={{
					fontStyle: 'normal',
					fontWeight: '600',
					fontSize: '16px',
					lineHeight: '24px',
					letterSpacing: '0.02em',
					textTransform: 'uppercase',
					color: '#002494'
				}}>
				Ընդունելության դիմում-հայտ
			</h1>
			<Divider />
			{!!modalForm && (
				<div className="form-steps">
					<Steps
						current={current}
						progressDot
						direction={isMobile ? 'horizontal' : 'vertical'}
						responsive={false}
						size="small"
						className="steps">
						{modalForm.map((item, i) => (
							<Step key={i} title={item.name} status={item.errorStatus ? 'error' : undefined} />
						))}
					</Steps>
					<div className="steps-content">
						<FormGroup
							formInitialValues={formInitialValues}
							tabFields={
								statusID === 1
									? modalForm[current]?.fields
									: [
											...modalForm[current].fields?.map(i => ({
												...i,
												disabledAt: ['ADD', 'EDIT'] as FixedLengthArray<
													['ADD' | 'EDIT', ('ADD' | 'EDIT')?]
												>,
												isRequired: false,
												disableDynamicActions: true
											}))
									  ]
							}
							formErrors={formErrors}
							visible={'EDIT'}
						/>
					</div>
				</div>
			)}
			<div className="control">
				<div key="prev">
					{isStepper && !!modalForm && current > 0 && (
						<Button className="main-btn" size="middle" onClick={prev} disabled={buttonLoading}>
							<CaretLeft size={14} weight="fill" style={{marginRight: 5}} /> Նախորդը
						</Button>
					)}
				</div>
				{isStepper && !!modalForm && current < modalForm.length - 1 && (
					<div key="next">
						<Button
							className="main-btn"
							size="middle"
							type="primary"
							onClick={next}
							disabled={buttonLoading}>
							Հաջորդը <CaretRight size={14} weight="fill" style={{marginLeft: 5}} />
						</Button>
					</div>
				)}
				{!!modalForm && current === modalForm.length - 1 && (
					<div key="submit">
						<Button
							disabled={statusID !== 1 || buttonLoading}
							className="main-btn"
							size="middle"
							type="primary"
							onClick={onSubmit}>
							Հաստատել (Submit)
						</Button>
					</div>
				)}
			</div>
		</Form>
	);
};
export default Admission;
